import { createContext, useContext, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import type { OrderWithItems } from "@/types/pos";
import { PosQueryKeys, PosQueryStaleTime } from "@/lib/pos/query";
import { apiFetch } from "@/utils";

interface PosOrderContextType {
  orders: OrderWithItems[];
  isLoading: boolean;
  refetch: () => void;
}

const PosOrderContext = createContext<PosOrderContextType | null>(null);

export function PosOrderProvider({ children, userId }: { children: React.ReactNode, userId: string }) {
  const { data: orders = [], isLoading, refetch: queryRefetch } = useQuery<OrderWithItems[]>({
    queryKey: [...PosQueryKeys.ordersByUser(userId)],
    queryFn: async () => {
      const res = await apiFetch(`/v1/pos/orders/${userId}/user`, { withCredentials: true });
      if (!res.ok) throw new Error("Failed to fetch orders");
      return res.json();
    },
    enabled: !!userId,
    staleTime: PosQueryStaleTime.orders,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const refetch = useCallback(() => {
    queryRefetch();
  }, [queryRefetch]);

  return (
    <PosOrderContext.Provider value={{ orders, isLoading, refetch }}>
      {children}
    </PosOrderContext.Provider>
  );
}

export function usePosOrders() {
  const ctx = useContext(PosOrderContext);
  if (!ctx) {
    throw new Error("usePosOrders must be used inside PosOrderProvider");
  }
  return ctx;
}
